import type { EventName, EventProps } from '@od-rwa/app-kit/observability/events';

interface Capture {
  /**
   * Posthog-js's `capture`, the one method the landing calls.
   */
  capture: (event: string, properties: object) => unknown;
}

interface QueuedEvent {
  /**
   * A name from app-kit's shared event catalogue.
   */
  event: EventName;

  /**
   * Its properties, as `track` received them.
   */
  properties: object;
}

// Events tracked before the client loads wait here, capped so a client that never loads holds little.
const MAX_QUEUED = 50;

let client: Capture | null = null;
let isSettled = false;
let queue: QueuedEvent[] = [];
let loading: Promise<void> | null = null;

/**
 * A throw inside the SDK is swallowed: analytics never breaks the page.
 */
function capture(target: Capture, event: EventName, properties: object): void {
  try {
    target.capture(event, properties);
  } catch {
    // Dropped on purpose; see above.
  }
}

/**
 * Queued until PostHog loads; on the local lane, or with no key, the event goes
 * nowhere.
 */
export function track<E extends EventName>(event: E, properties: EventProps<E>): void {
  if (client) {
    capture(client, event, properties);
    return;
  }
  if (isSettled || queue.length >= MAX_QUEUED) return;
  queue.push({ event, properties });
}

/**
 * Safe to call more than once: only the first call loads, so a failed load
 * warns once.
 */
export function loadObservability(): Promise<void> {
  loading ??= import('./internal/client')
    .then(({ createClient }) => {
      client = createClient();
      if (!client) return;
      for (const queued of queue) capture(client, queued.event, queued.properties);
    })
    .catch((error: unknown) => {
      // Offline, or a deploy swapped the chunk: the page works on without analytics.
      console.warn('[observability] PostHog did not load; events are dropped', error);
    })
    .finally(() => {
      isSettled = true;
      queue = [];
    });
  return loading;
}

/**
 * Loads PostHog once the browser is idle after first paint, so the page never
 * waits on analytics.
 */
export function startObservability(): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => void loadObservability(), { timeout: 3000 });
    return;
  }
  window.setTimeout(() => void loadObservability(), 1500);
}
